import { UserRole } from '@kfolks/types'
import { useAuth } from '@/hooks/useAuth'
import { AdminSnapshotWidget } from '../components/AdminSnapshotWidget'
import { CreateCtaButton } from '../components/CreateCtaButton'
import { CrossModuleCountsSection } from '../components/CrossModuleCountsSection'
import { MyOpenRequestsWidget } from '../components/MyOpenRequestsWidget'
import { MyStatsTilesWidget } from '../components/MyStatsTilesWidget'
import { PendingActionsCallout } from '../components/PendingActionsCallout'
import { PendingApprovalsWidget } from '../components/PendingApprovalsWidget'
import { QuickLinksWidget } from '../components/QuickLinksWidget'
import { RecentActivityWidget } from '../components/RecentActivityWidget'
import { UpcomingSlaWidget } from '../components/UpcomingSlaWidget'
import { ViewerReadOnlyHint } from '../components/ViewerReadOnlyHint'
import { WelcomeHeader } from '../components/WelcomeHeader'
import { useDashboard } from '../hooks/useDashboard'

/**
 * The new Request Center dashboard at `/dashboard` (Spec 4 / Group 9).
 *
 * Role-adaptive composition:
 *   - All roles get the welcome header.
 *   - VIEWER stops there + sees the read-only hint (zero data fetches).
 *   - USER / ADMIN / SUPER_ADMIN trigger the parallel-query composer
 *     (`useDashboard`) and render the widget grid; admin widgets self-gate
 *     via `useDashboard().isAdminViewer`.
 *
 * Spec: openspec/changes/request-center-dashboard-and-digest/specs/request-center-dashboard/spec.md
 */
export function RequestCenterDashboard() {
	const { user } = useAuth()
	if (!user) {
		// `<ProtectedRoute>` already gates unauthenticated traffic to /login;
		// this fallback shows nothing rather than crashing on a brief gap
		// while the session is rehydrating.
		return null
	}

	const isViewer = user.role === UserRole.VIEWER

	return (
		<div className="container mx-auto px-4 py-6 space-y-6">
			<WelcomeHeader displayName={user.displayName ?? user.email} role={user.role} />

			{isViewer ? <ViewerReadOnlyHint /> : <NonViewerDashboard />}
		</div>
	)
}

/**
 * Triggers `useDashboard()` (single point of parallel-query composition
 * across the page) and renders the visible widget grid for non-VIEWER
 * roles. Each widget reads from the shared TanStack Query cache —
 * widgets do not own their own fetches.
 *
 * Admin-only widgets self-gate via `useDashboard().isAdminViewer`:
 *   - PendingActionsCallout
 *   - AdminSnapshotWidget
 *   - CrossModuleCountsSection
 *   - QuickLinksWidget
 *
 * Approval-only widgets self-gate via the `pendingApprovalsVisible`
 * predicate (admin OR pendingApprovals > 0 OR hadEverPendingApproval):
 *   - PendingApprovalsWidget
 *   - UpcomingSlaWidget
 */
function NonViewerDashboard() {
	useDashboard()

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4">
				<PendingActionsCallout />
				<CreateCtaButton />
			</div>

			<MyStatsTilesWidget />

			<AdminSnapshotWidget />

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<MyOpenRequestsWidget />
				<RecentActivityWidget />
				<PendingApprovalsWidget />
				<UpcomingSlaWidget />
			</div>

			<CrossModuleCountsSection />

			<QuickLinksWidget />
		</div>
	)
}
